<template>
  <div class="relative bg-[color:var(--color-header-bottom-bg)]">
    <nav
      ref="bottomHeader"
      class="relative z-[2] flex min-h-[5.5rem] items-center justify-between gap-x-5 bg-[color:var(--color-header-bottom-bg)] px-5 py-3 xl:container lg:px-5 xl:mx-auto xl:px-5"
    >
      <router-link to="/">
        <VcImage :src="$cfg.logo_image" :alt="$context.storeName" class="-mt-4 h-16" lazy />
      </router-link>

      <!-- Innovadis custom navigation -->
      <div ref="navigation" class="flex items-center lg:ml-4" :class="!anonymousUsersAllowed ? 'mr-auto' : ''">
        <template v-for="(item, index) in mainMenu" :key="index">
          <div
            v-if="item.route === '#'"
            ref="showCatalogMenuButton"
            type="button"
            class="mr-4 flex cursor-pointer select-none items-center font-semibold hover:text-[color:var(--color-primary-500)]"
            :class="{ hidden: !anonymousUsersAllowed }"
            @click="catalogMenuVisible = !catalogMenuVisible"
          >
            <span>{{ item.title }}</span>

            <VcIcon :name="catalogMenuVisible ? 'chevron-up' : 'chevron-down'" size="xs" class="ml-1" />
          </div>

          <a v-else class="mr-4 font-bold hover:text-[color:var(--color-primary-500)]" :href="item.route as string">
            {{ item.title }}
          </a>
        </template>
      </div>

      <SearchBar v-if="anonymousUsersAllowed" />

      <ul v-if="anonymousUsersAllowed" class="-mx-2 flex items-center">
        <li v-for="item in desktopMainMenuItems" :key="item.id">
          <BottomHeaderLink v-if="item.id === 'compare'" :link="item" :count="productsIds.length">
            {{ item.title }}
          </BottomHeaderLink>

          <!-- Innovadis check cart amount -->
          <BottomHeaderLink
            v-else-if="item.id === 'cart' && !isServiceEmployee"
            :link="item"
            :count="cartFilteredItems?.length"
          >
            <span> {{ item.title }}</span>
          </BottomHeaderLink>
        </li>
      </ul>
    </nav>

    <!-- Catalog dropdown -->
    <transition
      v-if="catalogMenuItems.length"
      enter-from-class="-translate-y-full"
      leave-to-class="-translate-y-full"
      enter-active-class="will-change-transform"
      leave-active-class="will-change-transform"
    >
      <div
        v-if="catalogMenuVisible"
        ref="catalogMenuElement"
        class="absolute w-full overflow-y-scroll shadow-md transition-transform duration-200"
        :style="catalogMenuStyle"
      >
        <!-- Innovadis custom catalog menu -->
        <CatalogMenu :items="catalogMenuItems" @select="catalogMenuVisible = false" />
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { onClickOutside, syncRefs, useElementBounding, useScrollLock } from "@vueuse/core";
import { computed, ref, shallowRef } from "vue";
import { useNavigations } from "@/core/composables";
import { EXTRA_SERVICES_LIST } from "@/core/constants";
import { useUser } from "@/shared/account/composables/useUser";
import { useShortCart } from "@/shared/cart";
import { useCompareProducts } from "@/shared/compare";
import SearchBar from "../_internal/search-bar/search-bar.vue";
import BottomHeaderLink from "../_internal/bottom-header-link.vue";
import CatalogMenu from "../_internal/catalog-menu.vue";
import type { StyleValue } from "vue";
import { globals } from "@/core/globals";

const { isServiceEmployee, anonymousUsersAllowed } = useUser();
const { cart } = useShortCart();
const { desktopMainMenuItems, catalogMenuItems, mainMenu } = useNavigations();
const { productsIds } = useCompareProducts();
const { storeId } = globals;

const bottomHeader = ref<HTMLElement | null>(null);
const catalogMenuElement = shallowRef<HTMLElement | null>(null);
const showCatalogMenuButton = shallowRef<HTMLElement | null>(null);
const navigation = shallowRef<HTMLElement | null>(null);
const catalogMenuVisible = ref(false);

const { bottom } = useElementBounding(bottomHeader);

const cartFilteredItems = computed(() => cart.value?.items!.filter((item) => !EXTRA_SERVICES_LIST.includes(item.sku)));

const catalogMenuStyle = computed<StyleValue | undefined>(() =>
  bottom.value ? { maxHeight: `calc(100vh - ${bottom.value}px)` } : undefined,
);

onClickOutside(
  catalogMenuElement,
  () => {
    catalogMenuVisible.value = false;
  },
  { ignore: [navigation] },
);

syncRefs(catalogMenuVisible, useScrollLock(document.body));
</script>
